import { ACADEMY_TIMEZONE, DEFAULT_LOCALE } from './enums.js';
export function formatMoney(minorUnits, currency = 'AZN', locale = DEFAULT_LOCALE) {
    const major = minorUnits / 100;
    if (currency === 'AZN') {
        const digits = Number.isInteger(major) ? 0 : 2;
        return `${major.toFixed(digits)} ₼`;
    }
    return new Intl.NumberFormat(intlLocale(locale), {
        style: 'currency',
        currency,
        maximumFractionDigits: Number.isInteger(major) ? 0 : 2,
    }).format(major);
}
export function intlLocale(locale) {
    switch (locale) {
        case 'az':
            return 'az-AZ';
        case 'ru':
            return 'ru-RU';
        default:
            return 'en-GB';
    }
}
// Chrome lists az as supported but ships no az names, so Intl answers "M11 6, Fri".
const AZ_MONTHS = [
    'yanvar',
    'fevral',
    'mart',
    'aprel',
    'may',
    'iyun',
    'iyul',
    'avqust',
    'sentyabr',
    'oktyabr',
    'noyabr',
    'dekabr',
];
const AZ_MONTHS_SHORT = [
    'yan',
    'fev',
    'mar',
    'apr',
    'may',
    'iyn',
    'iyl',
    'avq',
    'sen',
    'okt',
    'noy',
    'dek',
];
const AZ_WEEKDAYS = [
    'bazar',
    'bazar ertəsi',
    'çərşənbə axşamı',
    'çərşənbə',
    'cümə axşamı',
    'cümə',
    'şənbə',
];
const AZ_WEEKDAYS_SHORT = ['B.', 'B.e.', 'Ç.a.', 'Ç.', 'C.a.', 'C.', 'Ş.'];
export function formatDate(date, locale, fields) {
    const { timeZone = ACADEMY_TIMEZONE, ...shown } = fields;
    if (locale !== 'az') {
        return new Intl.DateTimeFormat(intlLocale(locale), { ...shown, timeZone }).format(date);
    }
    const { year, month, day } = dateKeyParts(new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone,
    }).format(date));
    const months = shown.month === 'long' ? AZ_MONTHS : AZ_MONTHS_SHORT;
    const text = [shown.day && day, shown.month && months[month - 1], shown.year && year]
        .filter(Boolean)
        .join(' ');
    if (!shown.weekday)
        return text;
    const weekdays = shown.weekday === 'long' ? AZ_WEEKDAYS : AZ_WEEKDAYS_SHORT;
    const weekday = weekdays[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? '';
    if (text)
        return `${text}, ${weekday}`;
    return shown.weekday === 'short' ? weekday.toLocaleUpperCase('az-AZ') : weekday;
}
const AZ_UNITS = {
    second: 'saniyə',
    minute: 'dəqiqə',
    hour: 'saat',
    day: 'gün',
    week: 'həftə',
    month: 'ay',
    year: 'il',
};
export function formatRelative(value, unit, locale) {
    if (locale !== 'az') {
        return new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' }).format(value, unit);
    }
    const name = AZ_UNITS[unit];
    if (value === 0)
        return unit === 'second' ? 'indi' : `bu ${name}`;
    if (unit === 'day' && Math.abs(value) === 1)
        return value < 0 ? 'dünən' : 'sabah';
    if (Math.abs(value) === 1 && (unit === 'week' || unit === 'month' || unit === 'year')) {
        return `${value < 0 ? 'keçən' : 'gələn'} ${name}`;
    }
    return `${Math.abs(value)} ${name} ${value < 0 ? 'öncə' : 'ərzində'}`;
}
export function formatSlotLabel(iso, locale = DEFAULT_LOCALE, now = new Date()) {
    const date = new Date(iso);
    const time = new Intl.DateTimeFormat(intlLocale(locale), {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: ACADEMY_TIMEZONE,
    }).format(date);
    const dayKey = academyDateKey(date);
    const todayKey = academyDateKey(now);
    const tomorrowKey = academyDateKey(new Date(now.getTime() + 86_400_000));
    if (dayKey === todayKey)
        return `${relativeDayWord('today', locale)} · ${time}`;
    if (dayKey === tomorrowKey)
        return `${relativeDayWord('tomorrow', locale)} · ${time}`;
    const day = formatDate(date, locale, { weekday: 'short', day: 'numeric', month: 'short' });
    return `${day} · ${time}`;
}
function relativeDayWord(key, locale) {
    const table = {
        az: { today: 'Bu gün', tomorrow: 'Sabah' },
        en: { today: 'Today', tomorrow: 'Tomorrow' },
        ru: { today: 'Сегодня', tomorrow: 'Завтра' },
    };
    return table[locale][key];
}
export function academyDateKey(date) {
    return new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: ACADEMY_TIMEZONE,
    }).format(date);
}
export function formatTime(iso, locale = DEFAULT_LOCALE) {
    return new Intl.DateTimeFormat(intlLocale(locale), {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: ACADEMY_TIMEZONE,
    }).format(new Date(iso));
}
export function formatLongDate(iso, locale = DEFAULT_LOCALE) {
    return formatDate(new Date(iso), locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}
export function formatDateRangeTime(startIso, endIso, locale = DEFAULT_LOCALE) {
    return `${formatTime(startIso, locale)} – ${formatTime(endIso, locale)}`;
}
export function initialsOf(fullName) {
    return fullName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => [...part][0]?.toLocaleUpperCase('az-AZ') ?? '')
        .join('');
}
export function normalisePhone(input) {
    const digits = input.replace(/\D/g, '');
    if (digits.startsWith('994'))
        return `+${digits}`;
    if (digits.startsWith('0'))
        return `+994${digits.slice(1)}`;
    if (digits.length === 9)
        return `+994${digits}`;
    return `+${digits}`;
}
export function formatRelativeTime(iso, locale = DEFAULT_LOCALE, now = new Date()) {
    const target = new Date(iso);
    const dayDelta = academyDayDifference(now, target);
    if (dayDelta === 0) {
        const deltaMs = target.getTime() - now.getTime();
        if (Math.abs(deltaMs) >= 3_600_000) {
            return formatRelative(Math.trunc(deltaMs / 3_600_000), 'hour', locale);
        }
        return formatRelative(Math.trunc(deltaMs / 60_000), 'minute', locale);
    }
    const magnitude = Math.abs(dayDelta);
    if (magnitude >= 365)
        return formatRelative(Math.trunc(dayDelta / 365), 'year', locale);
    if (magnitude >= 30)
        return formatRelative(Math.trunc(dayDelta / 30), 'month', locale);
    if (magnitude >= 7)
        return formatRelative(Math.trunc(dayDelta / 7), 'week', locale);
    return formatRelative(dayDelta, 'day', locale);
}
export function dateKeyParts(key) {
    const [year, month, day] = key.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined || Number.isNaN(day)) {
        throw new RangeError(`Not a YYYY-MM-DD date key: ${key}`);
    }
    return { year, month, day };
}
export function academyDayDifference(from, to) {
    const asUtcMidnight = (date) => {
        const { year, month, day } = dateKeyParts(academyDateKey(date));
        return Date.UTC(year, month - 1, day);
    };
    return Math.round((asUtcMidnight(to) - asUtcMidnight(from)) / 86_400_000);
}
export function formatWeekdayShort(iso, locale = DEFAULT_LOCALE) {
    return formatDate(new Date(iso), locale, { weekday: 'short' })
        .replace('.', '')
        .toLocaleUpperCase(intlLocale(locale));
}
export function academyDayOfMonth(iso) {
    return Number(new Intl.DateTimeFormat('en-CA', { day: 'numeric', timeZone: ACADEMY_TIMEZONE }).format(new Date(iso)));
}
export function formatDayMonth(iso, locale = DEFAULT_LOCALE) {
    return formatDate(new Date(iso), locale, { day: 'numeric', month: 'long' });
}
export function formatShortDate(iso, locale = DEFAULT_LOCALE) {
    return formatDate(new Date(iso), locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    }).replace(/,/g, '');
}
export function formatMonthYear(iso, locale = DEFAULT_LOCALE) {
    return formatDate(new Date(iso), locale, { month: 'long', year: 'numeric' });
}
export function formatDateRange(startIso, endIso, locale = DEFAULT_LOCALE) {
    const part = (iso, fields) => formatDate(new Date(iso), locale, fields);
    const sameMonth = part(startIso, { month: 'long' }) === part(endIso, { month: 'long' });
    const sameYear = part(startIso, { year: 'numeric' }) === part(endIso, { year: 'numeric' });
    if (sameMonth && sameYear) {
        return `${part(startIso, { day: 'numeric' })} – ${part(endIso, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })}`;
    }
    if (sameYear) {
        return `${part(startIso, { day: 'numeric', month: 'long' })} – ${part(endIso, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })}`;
    }
    const full = { day: 'numeric', month: 'long', year: 'numeric' };
    return `${part(startIso, full)} – ${part(endIso, full)}`;
}
export function isoWeekNumber(iso) {
    const key = academyDateKey(new Date(iso));
    const { year, month, day } = dateKeyParts(key);
    const date = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayOfWeek);
    const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
    return Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7);
}
export function academyWeekStartKey(date = new Date()) {
    const key = academyDateKey(date);
    const { year, month, day } = dateKeyParts(key);
    const utc = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = utc.getUTCDay() || 7;
    utc.setUTCDate(utc.getUTCDate() - (dayOfWeek - 1));
    return utc.toISOString().slice(0, 10);
}
export function shiftDateKey(key, days) {
    const { year, month, day } = dateKeyParts(key);
    const utc = new Date(Date.UTC(year, month - 1, day));
    utc.setUTCDate(utc.getUTCDate() + days);
    return utc.toISOString().slice(0, 10);
}
//# sourceMappingURL=format.js.map